import { PageActivity } from '../activity';
import { ActivityState } from '@fullstory/renderer-shared';
import { Clock, WallClock } from '@fullstory/util/timer';
import { Constants, isUserActionEvent, RawEvent, RawEventKind } from '@fullstory/recording-api';

const NoHeartbeatThreshold = Constants.HeartbeatInterval + Constants.DefaultBundleUploadInterval;

/**
 * @deprecated use PageModel.ActivityState instead
 */
export class WebPageActivity implements PageActivity {
  private _activityState: ActivityState;
  private _lastActiveTime = 0; // These bits of state are retained after preProcessRawEvents(), so that
  private _lastHeartbeat = 0; // the _trailingState computation can be resumed when new bundles are received.
  private _pageOpen = true; // ...
  private _pageFocused = false; // ...

  constructor(private _clock: Clock = new WallClock()) {}

  activityState(): ActivityState {
    return this._activityState;
  }

  // Use this opportunity to figure out the trailing state of the page (i.e., page open,
  // active, focused, etc).
  update(events: RawEvent[], startTime: number, startIdx: number = 0) {
    for (let i = startIdx; i < events.length; i++) {
      const evt = events[i];
      if (!evt.FId) {
        // Update page state for root frame.

        // Note: There's no proper way to determine whether the window's focused on startup,
        // so we have to guess based on user actions (there's no initial 'window focus' event).
        if (isUserActionEvent(evt.Kind) || evt.Kind == RawEventKind.WINDOW_FOCUS) {
          this._pageFocused = true;
        }

        switch (evt.Kind) {
          case RawEventKind.WINDOW_BLUR:
            this._pageFocused = false;
            break;

          case RawEventKind.UNLOAD:
            this._pageOpen = false;
            break;

          case RawEventKind.HEARTBEAT:
            this._lastHeartbeat = evt.When;
            break;
        }
      }

      // Update page activity for all frames.
      if (isUserActionEvent(evt.Kind)) {
        this._lastActiveTime = evt.When;
      }
    }

    const now = this._clock.now() - startTime;

    let lastEventTime = 0;
    if (events.length > 0) {
      lastEventTime = events[events.length - 1].When;
    }

    // There are no user events for a certain period of time.
    const pageActive = lastEventTime - this._lastActiveTime < Constants.InactivityThreshold;

    // We're still receiving the expected heartbeats. Note that user actions reset Heartbeats.
    const heartBeating =
      now - Math.max(this._lastHeartbeat, this._lastActiveTime) < NoHeartbeatThreshold;

    // The page is still "alive", in the sense that user activity hasn't ceased for >30m.
    const pageAlive = now - lastEventTime < Constants.PageInactivityTimeout;

    if (!this._pageOpen) this._activityState = ActivityState.Closed;
    else if (!heartBeating || !pageAlive) this._activityState = ActivityState.Sleeping;
    else if (!this._pageFocused) this._activityState = ActivityState.Unfocused;
    else if (!pageActive) this._activityState = ActivityState.Inactive;
    else this._activityState = ActivityState.Active;
  }
}
